if (!customElements.get('norea-predictive-search')) {
  class NoreaPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.events || typeof fetch !== 'function' || typeof AbortController !== 'function' || typeof DOMParser !== 'function') return;
      this.input = this.querySelector('[data-norea-search-input]');
      this.panel = this.querySelector('[data-norea-search-panel]');
      this.results = this.querySelector('[data-norea-search-results]');
      this.state = this.querySelector('[data-norea-search-state]');
      this.status = this.querySelector('[data-norea-search-status]');
      if (!this.input || !this.panel || !this.results || !this.state || !this.status || !this.dataset.predictiveUrl) return;
      this.listID = `${this.input.id}-results`;
      this.listLabel = this.results.querySelector('ul[role="listbox"]')?.getAttribute('aria-label') || '';
      this.version = 0;
      this.options = [];
      this.active = -1;
      this.events = new AbortController();
      const listenerOptions = { signal: this.events.signal };
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-controls', this.listID);
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('autocomplete', 'off');
      this.input.addEventListener('input', event => { if (!event.isComposing && !this.composing) this.queue(); }, listenerOptions);
      this.input.addEventListener('focus', () => this.queue(), listenerOptions);
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.close(); }, listenerOptions);
      this.input.addEventListener('compositionend', () => { this.composing = false; this.queue(); }, listenerOptions);
      this.input.addEventListener('keydown', event => this.onKey(event), listenerOptions);
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.close(); }, listenerOptions);
      this.querySelector('[data-norea-search-form]')?.addEventListener('submit', () => this.close(), listenerOptions);
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, listenerOptions);
    }

    disconnectedCallback() {
      this.close();
      this.events?.abort();
      this.events = null;
    }

    close() {
      clearTimeout(this.timer);
      clearTimeout(this.deadline);
      this.timer = null;
      this.deadline = null;
      this.request?.abort();
      this.request = null;
      this.version = (this.version || 0) + 1;
      this.active = -1;
      this.options?.forEach(option => option.setAttribute('aria-selected', 'false'));
      this.input?.setAttribute('aria-expanded', 'false');
      this.input?.removeAttribute('aria-activedescendant');
      this.results?.setAttribute('aria-busy', 'false');
      if (this.panel) this.panel.hidden = true;
      if (this.status) this.status.textContent = '';
    }

    queue() {
      this.close();
      this.resetResults();
      const query = this.input.value.trim();
      if (query.length < 2) return;
      const version = this.version;
      this.timer = setTimeout(() => { this.timer = null; this.load(query, version); }, 180);
    }

    resetResults() {
      this.options = [];
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      const list = document.createElement('ul');
      list.setAttribute('id', this.listID);
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', this.listLabel);
      list.className = 'norea-predictive__list';
      this.results.replaceChildren(list);
    }

    showError() {
      this.resetResults();
      this.results.setAttribute('aria-busy', 'false');
      this.state.hidden = false;
      this.state.textContent = this.dataset.error;
      this.status.textContent = this.dataset.error;
    }

    current(query, version) {
      return this.isConnected && version === this.version && query === this.input.value.trim() && this.contains(document.activeElement);
    }

    async load(query, version) {
      if (!this.current(query, version)) return;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.results.setAttribute('aria-busy', 'true');
      this.state.hidden = false;
      this.state.textContent = this.dataset.loading;
      this.status.textContent = this.dataset.loading;
      this.request = new AbortController();
      const request = this.request;
      const deadline = setTimeout(() => {
        request.abort();
        if (this.current(query, version)) this.showError();
      }, 8000);
      this.deadline = deadline;
      try {
        const url = new URL(this.dataset.predictiveUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid search route');
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'norea-predictive-search');
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url.toString(), { signal: request.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Suggestions unavailable');
        const html = await response.text();
        if (!this.current(query, version) || request.signal.aborted) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-norea-predictive-results]');
        if (!source?.querySelector('ul[role="listbox"]')) throw new Error('Missing search section');
        const content = document.importNode(source, true);
        const list = content.querySelector('ul[role="listbox"]');
        list.id = this.listID;
        this.options = [...list.querySelectorAll('[role="option"]')];
        this.options.forEach((option, index) => { option.id = `${this.listID}-option-${index}`; option.setAttribute('aria-selected', 'false'); });
        this.results.replaceChildren(content);
        this.results.setAttribute('aria-busy', 'false');
        this.state.hidden = true;
        this.status.textContent = content.dataset.announcement;
        this.resultQuery = query;
      } catch (error) {
        if (!this.current(query, version) || request.signal.aborted) return;
        this.showError();
      } finally {
        clearTimeout(deadline);
        if (this.deadline === deadline) this.deadline = null;
        if (this.request === request) this.request = null;
      }
    }

    onKey(event) {
      if (event.isComposing || this.composing) return;
      if (event.key === 'Escape' && (!this.panel.hidden || this.timer || this.request)) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key === 'Enter' && !this.panel.hidden && this.active >= 0) {
        const link = this.options[this.active]?.querySelector('a[href]');
        if (link) { event.preventDefault(); link.click(); this.close(); }
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.options.length === 0 || this.resultQuery !== this.input.value.trim()) return;
      event.preventDefault();
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (event.key === 'ArrowDown') this.active = Math.min(this.active + 1, this.options.length - 1);
      else this.active = this.active < 0 ? this.options.length - 1 : Math.max(this.active - 1, 0);
      this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
      const option = this.options[this.active];
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('norea-predictive-search', NoreaPredictiveSearch);
}
